import { useEffect, useRef, useState } from 'react'
import { useRollbar } from '@rollbar/react'
import { FetchUserFilesFnType } from '../types'
import { DataFile } from 'client/store/files/filesReducer'
import axios, { CancelTokenSource } from 'axios'
import { LogArgument } from 'rollbar'
import { NetworkError } from 'common/errors/NetworkError'

interface UseFileLibraryProps {
  files: Record<string, DataFile>
  fileType?: string
  fetchData: FetchUserFilesFnType
  firstFetchLimit?: number
  additionalFetchLimit?: number
}

export const useFileLibrary = ({
  files,
  fileType,
  fetchData,
  firstFetchLimit = 30,
  additionalFetchLimit = 10,
}: UseFileLibraryProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [isFetching, setIsFetching] = useState(false)
  const [cancelTokenSourceState, setCancelTokenSourceState] =
    useState<null | CancelTokenSource>(null)

  const rollbar = useRollbar()

  const fileListRef = useRef<HTMLDivElement>(null)
  const previousScrollTopRef = useRef(0)
  const countForLoadingRef = useRef(30)
  const filesKeysRef = useRef(Object.keys(files))

  const getLastId = () => filesKeysRef.current[0]

  const normalizeCountForLoading = (
    value: number,
    step = additionalFetchLimit,
    min = firstFetchLimit,
  ): number => {
    const rounded = Math.ceil(value / step) * step
    return Math.max(min, rounded)
  }

  const onScroll = async () => {
    const fileList = fileListRef.current
    if (
      isFetching ||
      filesKeysRef.current.length !== countForLoadingRef.current ||
      !fileList
    ) {
      return
    }
    const currentScrollTop = fileList.scrollTop
    const isScrollingDown = currentScrollTop > previousScrollTopRef.current
    previousScrollTopRef.current = currentScrollTop
    const isAtBottom =
      currentScrollTop + fileList.clientHeight >= fileList.scrollHeight - 50

    if (isScrollingDown && isAtBottom) {
      try {
        setIsFetching(true)
        await fetchData(
          fileType,
          getLastId(),
          additionalFetchLimit,
          searchValue,
        )
      } catch (e) {
        if (e instanceof NetworkError) {
          // do nothing
        } else {
          rollbar.error(e as LogArgument)
        }
      } finally {
        setIsFetching(false)
      }
    }
  }

  const onSearchChange = async (searchValue: string) => {
    setSearchValue(searchValue)
    const cancelTokenSource = axios.CancelToken.source()
    if (cancelTokenSourceState) {
      cancelTokenSourceState.cancel()
    }
    setCancelTokenSourceState(cancelTokenSource)
    try {
      setIsFetching(true)
      await fetchData(
        fileType,
        null,
        firstFetchLimit,
        searchValue,
        cancelTokenSource.token,
      )
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: <explanation>
      console.log('--e--', error)
    } finally {
      setIsFetching(false)
    }
  }

  useEffect(() => {
    const fileList = fileListRef.current
    fileList?.addEventListener('scroll', onScroll)
    return () => {
      fileList?.removeEventListener('scroll', onScroll)
      onSearchChange('')
    }
  }, [])

  useEffect(() => {
    filesKeysRef.current = Object.keys(files)
    countForLoadingRef.current = normalizeCountForLoading(
      Object.keys(files).length,
    )
  }, [files])

  return {
    isFetching,
    onSearchChange,
    fileListRef,
    searchValue,
  }
}
